import { useEffect, useState } from "react";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

type HomeContent = {
  heroTitle: string;
  heroEmphasis: string;
  heroDescription: string;
  heroImageUrl: string | null;
  promoTitle: string;
  promoDescription: string;
  promoImageUrl: string | null;
};

export function WhiteLabelHomepageEditor({ storeId, siteUrl }: { storeId: number; siteUrl: string }) {
  const [content, setContent] = useState<HomeContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const [promoUploading, setPromoUploading] = useState(false);
  const uploading = heroUploading || promoUploading;
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`/api/order/stores/${storeId}/white-label-homepage`, { cache: "no-store" })
      .then(async response => { if (!response.ok) throw Error("Could not load homepage"); return response.json(); })
      .then(data => { if (active) { setContent(data.content); setMessage(""); } })
      .catch(() => { if (active) setMessage("Could not load the homepage. Please try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [storeId]);

  async function save() {
    if (!content) return;
    setSaving(true); setMessage("");
    try {
      const token = localStorage.getItem("ayrshire_token");
      const response = await fetch(`/api/order/stores/${storeId}/white-label-homepage`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ content }),
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.error || "Could not save homepage");
      setContent(data.content);
      setMessage("Homepage saved. Refresh the website to see your changes.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not save homepage"); }
    finally { setSaving(false); }
  }

  const text = (field: keyof HomeContent, value: string) => setContent(current => current ? { ...current, [field]: value } : current);
  return <section className="rounded-2xl border border-violet-200 bg-white p-5 space-y-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div><h3 className="font-bold text-violet-950">Branded homepage</h3><p className="text-sm text-slate-600">Edit the words and photos on your shop website. Products and category photos are managed in Menu; deals are managed in Offers.</p></div>
      <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-violet-800 underline">View website ↗</a>
    </div>
    {loading && <p className="text-sm text-slate-600">Loading homepage…</p>}
    {content && <>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="block text-sm font-medium">Main headline<Input className="mt-1" maxLength={90} value={content.heroTitle} onChange={e => text("heroTitle", e.target.value)} /></label>
        <label className="block text-sm font-medium">Highlighted headline<Input className="mt-1" maxLength={90} value={content.heroEmphasis} onChange={e => text("heroEmphasis", e.target.value)} /></label>
      </div>
      <label className="block text-sm font-medium">Introductory text<Textarea className="mt-1" maxLength={400} value={content.heroDescription} onChange={e => text("heroDescription", e.target.value)} rows={3} /></label>
      <div><p className="text-sm font-medium mb-1">Main homepage photo</p><ImageUpload value={content.heroImageUrl ?? ""} onChange={url => text("heroImageUrl", url ?? "")} onUploadingChange={setHeroUploading} /><p className="text-xs text-slate-500 mt-1">This photo sits beside the main headline. Use a wide image.</p></div>
      <div className="border-t pt-4 space-y-4">
        <h4 className="font-semibold">Bottom promotion</h4>
        <label className="block text-sm font-medium">Heading<Input className="mt-1" maxLength={120} value={content.promoTitle} onChange={e => text("promoTitle", e.target.value)} /></label>
        <label className="block text-sm font-medium">Text<Textarea className="mt-1" maxLength={400} value={content.promoDescription} onChange={e => text("promoDescription", e.target.value)} rows={2} /></label>
        <div><p className="text-sm font-medium mb-1">Promotion photo (optional)</p><ImageUpload value={content.promoImageUrl ?? ""} onChange={url => text("promoImageUrl", url ?? "")} onUploadingChange={setPromoUploading} /></div>
      </div>
      <Button type="button" disabled={saving || uploading} onClick={() => void save()}>{saving ? "Saving…" : uploading ? "Uploading photo…" : "Save homepage"}</Button>
    </>}
    {message && <p className="text-sm" role="status">{message}</p>}
  </section>;
}
